import * as React from "react";
import { ItemRenderer, ItemRendererProps } from "@blueprintjs/select";
import { MultiSelect } from "./MultiSelect";
import { MenuItem, MenuItemProps } from "@blueprintjs/core";
import { useCallback } from "react";
import { IUser } from "../../../api/admin";
import { useSelectedItems } from "../../../hooks/useSelectedItems";
import { cappedItemListRenderer } from "./cappedItemListRenderer";

const filterUser = (query: string, user: IUser) =>
  user.name.toLowerCase().includes(query.toLowerCase()) ||
  user.email.toLowerCase().includes(query.toLowerCase());

const areUsersEqual = (user1: IUser, user2: IUser) => user1.id === user2.id;

const renderTag = (user: IUser) => user.name;

interface FolderUserMultiSelectProps {
  selectedUserIds: number[];
  users: IUser[];
  onUserChange: (userIds: number[]) => void;
  inputRef: React.RefObject<HTMLInputElement>;
}

export function FolderUserMultiSelect({
  selectedUserIds,
  users,
  onUserChange,
  inputRef,
}: FolderUserMultiSelectProps) {
  const { idSet: selectedUserIdSet, selectedItems } = useSelectedItems(
    selectedUserIds,
    users
  );

  const handleUserRemove = useCallback(
    (user: IUser) => {
      const newUserIds = selectedUserIds.filter((id: number) => id !== user.id);
      onUserChange(newUserIds);
    },
    [selectedUserIds, onUserChange]
  );

  const handleUserSelect = useCallback(
    (user: IUser) => {
      if (selectedUserIdSet.has(user.id)) {
        handleUserRemove(user);
      } else {
        const newUserIds = [...selectedUserIds, user.id];
        onUserChange(newUserIds);
      }
    },
    [selectedUserIds, selectedUserIdSet, onUserChange, handleUserRemove]
  );

  const renderUser: ItemRenderer<IUser> = useCallback(
    (user, rendererProps) => {
      if (!rendererProps.modifiers.matchesPredicate) {
        return null;
      }
      const isSelected = selectedUserIdSet.has(user.id);

      return (
        <MenuItem
          {...getUserItemProps(user, rendererProps, isSelected)}
          roleStructure="listoption"
          shouldDismissPopover={false}
          text={user.name}
        />
      );
    },
    [selectedUserIdSet]
  );

  return (
    <MultiSelect
      items={users}
      selectedItems={selectedItems}
      itemRenderer={renderUser}
      itemListRenderer={cappedItemListRenderer}
      itemPredicate={filterUser}
      itemsEqual={areUsersEqual}
      onItemSelect={handleUserSelect}
      onRemove={handleUserRemove}
      placeholder="Add users to folder..."
      disabled={!users || users.length === 0}
      popoverProps={{
        minimal: true,
        matchTargetWidth: true,
      }}
      tagRenderer={renderTag}
      tagInputProps={{
        inputRef,
        tagProps: {
          minimal: false,
        },
      }}
      resetOnSelect={true}
    />
  );
}

export function getUserItemProps(
  user: IUser,
  { handleClick, handleFocus, modifiers, query, ref }: ItemRendererProps,
  isSelected?: boolean
): MenuItemProps & React.Attributes {
  return {
    active: modifiers.active,
    disabled: modifiers.disabled,
    key: user.id,
    onClick: handleClick,
    onFocus: handleFocus,
    ref,
    selected: isSelected,
    text: user.name,
    label: user.email,
  };
}
